import { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";

// Components
import {
  Button,
  Search,
  CentralModal,
  Card,
  DataTable,
  Header,
  Body
} from "../../../../UI";
import { Heading2 } from "../../../../typography";
import ConfirmDialog from "../../../../UI/ConfirmDialog/ConfirmDialog";
import StaffManagementItem from "../../../../lists/generalBranchSettings/StaffManagement.item";

// Containers
import AddStaff from "../../../../../containers/forms/generalBranchSettingsForms/StaffManagement/AddStaff/AddStaff";
import EditStaff from "../../../../../containers/forms/generalBranchSettingsForms/StaffManagement/EditStaff/EditStaff";

// Constants

// Builders
import * as buttonDesignType from "../../../../../builders/buttonDesign.types";
import { StaffManagementTableHeadBuilder } from "../../../../../builders/generalBranchSettings.builder";

// Calls
import {
  fetchRolesRequest,
  fetchStaffMembersRequest,
  deleteStaffMemberRequest
} from "../../../../../api/calls/general-branch-settings.call";

// Utils
import { isEmpty } from "../../../../../utils";

// Styles
import { StaffManagementStyles } from "./staffManagement.styles";

const ADD_STAFF = "ADD_STAFF";
const EDIT_STAFF = "EDIT_STAFF";

const StaffManagement = ({ branchId }) => {
  const [staffManagmentSectionState, setStaffManagmentSectionState] = useState({
    modalType: undefined,
    chosenStaffMember: undefined,
    staffMembers: [],
    roles: [],
    searchQuery: ""
  });

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchStaffMemebersAndRoles = useCallback(async () => {
    const rolesAndStaffMembersResponse = await Promise.all([
      fetchRolesRequest(branchId),
      fetchStaffMembersRequest(branchId)
    ]);

    const rolesResponse = rolesAndStaffMembersResponse[0];
    const staffMembersResponse = rolesAndStaffMembersResponse[1];
    if (rolesResponse.hasError) {
      toast.error(rolesResponse.error.message);
      return;
    }

    if (staffMembersResponse.hasError) {
      toast.error(staffMembersResponse.error.message);
      return;
    }
    setStaffManagmentSectionState(prevValues => ({
      ...prevValues,
      staffMembers: staffMembersResponse.staffMembers,
      roles: rolesResponse.roles.map(role => {
        return { ...role, id: parseInt(role.id) };
      })
    }));
  }, [branchId]);

  useEffect(() => {
    fetchStaffMemebersAndRoles();
  }, [fetchStaffMemebersAndRoles]);

  const confirmDeleteStaffMember = async () => {
    if (!deleteTarget) return;
    const targetId = deleteTarget;
    const response = await deleteStaffMemberRequest(branchId, targetId);
    setDeleteTarget(null);
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    toast.success("Staff member successfully deleted.");
    setDeletingId(targetId);
    setTimeout(() => {
      setDeletingId(null);
      setStaffManagmentSectionState(prevValues => ({
        ...prevValues,
        staffMembers: response.staffMembers
      }));
    }, 300);
  };

  const addStaffMemberOpenModal = () => {
    setStaffManagmentSectionState(prevValues => ({
      ...prevValues,
      modalType: ADD_STAFF
    }));
  };

  const editStaffMemberOpenModal = member => {
    setStaffManagmentSectionState(prevValues => ({
      ...prevValues,
      modalType: EDIT_STAFF,
      chosenStaffMember: member
    }));
  };

  const closeModal = () => {
    setStaffManagmentSectionState(prevValues => ({
      ...prevValues,
      modalType: undefined,
      chosenStaffMember: undefined
    }));
  };

  const setStaffMembersAfterCreateOrEdit = staffMembers => {
    setStaffManagmentSectionState(prevValues => ({
      ...prevValues,
      staffMembers,
      modalType: undefined,
      chosenStaffMember: undefined
    }));
  };

  const handleSearch = e => {
    setStaffManagmentSectionState(prevValues => ({
      ...prevValues,
      searchQuery: e.target.value
    }));
  };

  const filterStaff = query => {
    const q = query.toLowerCase();
    return staffManagmentSectionState.staffMembers.filter(
      staff =>
        staff.firstName.toLowerCase().includes(q) ||
        staff.lastName.toLowerCase().includes(q)
    );
  };

  return (
    <StaffManagementStyles>
      <Card rounded className="headerCard">
        <Header>
          <div className="header">
            <Heading2>STAFF MANAGEMENT</Heading2>
            <Button
              onClick={addStaffMemberOpenModal}
              className="addStaff"
              buttonDesignType={buttonDesignType.PRIMARY}
            >
              Add Staff
            </Button>
          </div>
        </Header>
        <Body>
          <div className="searchBar">
            <div className="searchContainer">
              <Search
                placeholder="Search members"
                value={staffManagmentSectionState.searchQuery}
                onChange={handleSearch}
              />
            </div>
          </div>
        </Body>
      </Card>
      {!isEmpty(staffManagmentSectionState.staffMembers) && (
        <Card rounded className="tableCard">
          <DataTable
            columns={StaffManagementTableHeadBuilder}
            data={filterStaff(staffManagmentSectionState.searchQuery)}
            rowClassName={item => deletingId === item.id ? "row-deleting" : ""}
            renderRow={staff => (
              <StaffManagementItem
                {...staff}
                deleteAction={() => setDeleteTarget(staff.id)}
                editAction={() => editStaffMemberOpenModal(staff)}
              />
            )}
          />
        </Card>
      )}
      <CentralModal
        isOpen={!!staffManagmentSectionState.modalType}
        closeModal={closeModal}
        width={620}
      >
        <Card>
          {staffManagmentSectionState.modalType === ADD_STAFF && (
            <AddStaff
              setStaffMembersAfterCreateOrEdit={
                setStaffMembersAfterCreateOrEdit
              }
              branchId={branchId}
              roles={staffManagmentSectionState.roles}
              closeModal={closeModal}
            />
          )}
          {staffManagmentSectionState.modalType === EDIT_STAFF && (
            <EditStaff
              setStaffMembersAfterCreateOrEdit={
                setStaffMembersAfterCreateOrEdit
              }
              chosenStaffMember={staffManagmentSectionState.chosenStaffMember}
              branchId={branchId}
              roles={staffManagmentSectionState.roles}
              closeModal={closeModal}
            />
          )}
        </Card>
      </CentralModal>
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDeleteStaffMember}
        title="Delete Staff Member?"
        message="Are you sure you want to delete this staff member?"
      />
    </StaffManagementStyles>
  );
};

export default StaffManagement;
